<template>
  <div class="image-editor-content h-100">
    <div class="mdr-image-editor-toolbar">
      <button
        v-if="buttons?.includes('dash')"
        @click="strokeType = 'dash'"
        :class="['ico-btn', 'draw-stroke', { active: strokeType === 'dash' }]"
        type="button"
      />
      <button
        v-if="buttons?.includes('line')"
        @click="strokeType = 'line'"
        :class="['ico-btn', 'draw-arrow', { active: strokeType === 'line' }]"
        type="button"
      />
      <button
        v-if="buttons?.includes('reset')"
        @click="drawingCanvas?.reset()"
        class="ico-btn clear-drawing"
        type="button"
      />
      <span
        v-if="
          !!find(['dash', 'line', 'reset'], b => buttons?.includes(b)) &&
            !!find(['undo', 'redo', 'rotateLeft', 'rotateRight'], b =>
              buttons?.includes(b)
            )
        "
        class="spacer"
      ></span>
      <button
        v-if="buttons?.includes('undo')"
        @click="drawingCanvas?.undo()"
        class="ico-btn undo"
        type="button"
      />
      <button
        v-if="buttons?.includes('redo')"
        @click="drawingCanvas?.redo()"
        class="ico-btn redo"
        type="button"
      />
      <button
        v-if="buttons?.includes('rotateLeft')"
        class="ico-btn rotate-left"
        type="button"
      />
      <button
        v-if="buttons?.includes('rotateRight')"
        class="ico-btn rotate-right"
        type="button"
      />
      <slot />
    </div>
    <div class="mdr-image-editor-image-container">
      <div
        ref="drawingCanvasContainer"
        class="mdr-image-editor-image-container-inner"
      >
        <div
          v-if="
            image &&
              drawingCanvasContainerSize &&
              imageSize.width &&
              imageSize.height
          "
          class="canvas-frame"
        >
          <vue-drawing-canvas
            ref="drawingCanvas"
            v-model:image="editedImage"
            :width="
              (drawingCanvasContainerSize.width /
                drawingCanvasContainerSize.height >
              imageSize.width / imageSize.height
                ? drawingCanvasContainerSize.width *
                  (imageSize.width /
                    imageSize.height /
                    (drawingCanvasContainerSize.width /
                      drawingCanvasContainerSize.height))
                : drawingCanvasContainerSize.width) - 2
            "
            :height="
              (drawingCanvasContainerSize.height /
                drawingCanvasContainerSize.width >
              imageSize.height / imageSize.width
                ? drawingCanvasContainerSize.height *
                  (imageSize.height /
                    imageSize.width /
                    (drawingCanvasContainerSize.height /
                      drawingCanvasContainerSize.width))
                : drawingCanvasContainerSize.height) - 2
            "
            :stroke-type="strokeType"
            :line-cap="'round'"
            :line-join="'round'"
            :fill-shape="false"
            :eraser="false"
            :lineWidth="3"
            :color="'#FF0000'"
            :background-color="'#FFFFFF'"
            :background-image="image"
            :initial-image="[]"
            saveAs="jpeg"
            :styles="{}"
            :lock="false"
            :additional-images="[]"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { onMounted, reactive, ref, watch } from "vue";
import VueDrawingCanvas from "vue-drawing-canvas";
import _ from "lodash";
import imageCompression from "browser-image-compression";
import { useTimeoutFn } from "@vueuse/core";

export default {
  props: {
    modelValue: String,
    width: Number,
    height: Number,
    buttons: {
      type: Array,
      required: false,
      default() {
        return [
          "dash",
          "line",
          "reset",
          "undo",
          "redo",
          "rotateLeft",
          "rotateRight"
        ];
      }
    }
  },
  emits: ["update:modelValue"],
  components: {
    VueDrawingCanvas
  },
  setup(props: object, context: { emit: Function }) {
    const image = (props as { modelValue: string }).modelValue;

    const imageSize = reactive<Record<"width" | "height", number | null>>({
      width: (props as { width?: number; height?: number }).width ?? null,
      height: (props as { width?: number; height?: number }).height ?? null
    });

    onMounted(async () => {
      if (imageSize.width && imageSize.height) {
        return;
      }

      const imgHtml = await imageCompression.loadImage(image);
      imageSize.width = imgHtml.width;
      imageSize.height = imgHtml.height;
    });

    const drawingCanvasContainer = ref(),
      drawingCanvasContainerSize = ref<{
        width: number;
        height: number;
      } | null>(null);

    watch(
      () => drawingCanvasContainer.value,
      v => {
        if (v) {
          drawingCanvasContainerSize.value = {
            width: v.clientWidth,
            height: v.clientHeight
          };
        }
      },
      { immediate: true }
    );

    const strokeType = ref<"dash" | "line">("dash");

    const drawingCanvas = ref();

    const editedImage = ref("");

    const {
      start: startModelUpdatingTimer,
      stop: stopModelUpdatingTimer
    } = useTimeoutFn(
      () => context.emit("update:modelValue", editedImage.value),
      300,
      {
        immediate: false
      }
    );

    watch(
      () => editedImage.value,
      v => {
        stopModelUpdatingTimer();

        if (drawingCanvas.value?.isEmpty()) {
          context.emit("update:modelValue", image);
        } else {
          startModelUpdatingTimer();
        }
      }
    );

    return {
      editedImage,
      image,
      strokeType,
      drawingCanvas,
      drawingCanvasContainer,
      drawingCanvasContainerSize,
      imageSize,
      ..._.pick(_, "find"),
      ...props
    };
  }
};
</script>

<style lang="scss">
@import "@/assets/styles/image-editor";
</style>
